import { FolderOpen, FileText, Settings, LogOut, Home, Search } from 'lucide-react';
import Logo from './Logo';

/**
 * Simplified Navigation - Clean, focused workflow
 * Flow: Find Jobs → Tailor Resume → My Applications
 */
function Navigation({
  view,
  onNavigate,
  onLogout,
  onOpenSettings,
  userName,
  applicationCount = 0,
  showUpgrade = false,
  onUpgrade = () => {},
  creditsRemaining = null,
}) {
  // Desktop nav - ordered by workflow with Dashboard as home
  const desktopNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'jobs', label: 'Find Jobs', icon: Search },
    { id: 'tailor', label: 'Tailor Resume', icon: FileText, primary: true },
    { id: 'library', label: 'Applications', icon: FolderOpen, badge: applicationCount },
  ];

  // Mobile nav items
  const mobileNavItems = [
    { id: 'dashboard', label: 'Home', icon: Home },
    { id: 'jobs', label: 'Jobs', icon: Search },
    { id: 'tailor', label: 'Tailor', icon: FileText, primary: true },
    { id: 'library', label: 'Apps', icon: FolderOpen, badge: applicationCount },
  ];

  return (
    <>
      {/* Desktop Navigation - Top */}
      <nav className="hidden sm:block sticky top-2 md:top-4 z-40 mb-4 md:mb-6">
        <div className="max-w-7xl mx-auto px-2 sm:px-4">
          <div className="bg-white border border-slate-200/80 rounded-xl shadow-md shadow-slate-200/50">
            <div className="flex items-center justify-between px-4 py-3">
              {/* Logo */}
              <button
                onClick={() => onNavigate('dashboard')}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Logo size={32} />
                <span className="font-heading text-base font-bold text-slate-900">resmaker.ai</span>
              </button>

              {/* Navigation Items */}
              <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
                {desktopNavItems.map((item) => {
                  const Icon = item.icon;
                  // Map related views to their nav items
                  const isActive = view === item.id ||
                    (item.id === 'tailor' && view === 'tailored') ||
                    (item.id === 'library' && view === 'offers');
                  return (
                    <button
                      key={item.id}
                      onClick={() => onNavigate(item.id)}
                      className={`
                        relative flex items-center justify-center gap-2
                        min-h-[36px] px-4 py-1.5
                        rounded-md font-medium text-sm
                        transition-all duration-150 cursor-pointer
                        ${isActive
                          ? 'bg-white text-primary shadow-sm ring-1 ring-primary/20'
                          : item.primary
                            ? 'text-primary hover:bg-white/60'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
                        }
                      `}
                    >
                      <Icon size={16} className={isActive ? 'text-primary' : ''} />
                      <span className="hidden md:inline">{item.label}</span>
                      {item.badge > 0 && (
                        <span className={`
                          hidden md:inline px-1.5 py-0.5 rounded text-xs font-medium
                          ${isActive ? 'bg-primary/10 text-primary' : 'bg-slate-200 text-slate-600'}
                        `}>
                          {item.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Right side - Credits & Settings */}
              <div className="flex items-center gap-2">
                {showUpgrade && creditsRemaining != null && (
                  <button
                    onClick={onUpgrade}
                    className={`hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                      creditsRemaining <= 0
                        ? 'bg-primary text-white hover:bg-dark'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                    title={creditsRemaining > 0 ? 'Click to add more credits' : 'Get credits to continue'}
                  >
                    <span className={`w-2 h-2 rounded-full ${
                      creditsRemaining <= 0 ? 'bg-white' : creditsRemaining <= 2 ? 'bg-slate-400' : 'bg-green-500'
                    }`} />
                    {creditsRemaining <= 0 ? 'Add Credits' : `${creditsRemaining}`}
                  </button>
                )}
                <button
                  onClick={onOpenSettings}
                  className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                  title="Settings"
                >
                  <Settings size={18} />
                </button>
                <button
                  onClick={onLogout}
                  className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                  title="Logout"
                >
                  <LogOut size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation - Bottom */}
      <nav className="sm:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/98 backdrop-blur-xl border-t border-slate-200/80 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="flex items-center justify-around px-2 py-1">
          {mobileNavItems.map((item) => {
            const Icon = item.icon;
            // Map related views to their nav items
            const isActive = view === item.id ||
              (item.id === 'tailor' && view === 'tailored') ||
              (item.id === 'library' && view === 'offers');
            return (
              <button
                key={item.id}
                onClick={() => onNavigate(item.id)}
                className={`
                  relative flex flex-col items-center justify-center gap-0.5
                  min-h-[56px] min-w-[64px] px-2 py-1.5
                  rounded-xl font-medium text-[10px]
                  transition-all duration-150 ease-out cursor-pointer
                  active:scale-95
                  ${isActive
                    ? 'text-primary font-semibold'
                    : 'text-slate-500 hover:text-slate-700 active:bg-slate-100'
                  }
                `}
              >
                <div className={`
                  relative p-1.5 rounded-lg transition-all duration-150
                  ${isActive ? 'bg-primary/15' : ''}
                `}>
                  <Icon size={20} strokeWidth={isActive ? 2.5 : 2} />
                  {item.badge > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] flex items-center justify-center bg-primary text-white text-[9px] font-bold rounded-full px-0.5">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </div>
                <span>{item.label}</span>
                {isActive && (
                  <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-4 h-1 bg-primary rounded-full" />
                )}
              </button>
            );
          })}
          <button
            onClick={onOpenSettings}
            className="relative flex flex-col items-center justify-center gap-0.5 min-h-[56px] min-w-[64px] px-2 py-1.5 rounded-xl font-medium text-[10px] text-slate-500 hover:text-slate-700 active:bg-slate-100 active:scale-95 transition-all duration-150 cursor-pointer"
          >
            <div className="p-1.5">
              <Settings size={20} strokeWidth={2} />
            </div>
            <span>Settings</span>
          </button>
        </div>
      </nav>

      {/* Mobile Header - Minimal */}
      <header className="sm:hidden sticky top-0 z-40 bg-white/95 backdrop-blur-lg border-b border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => onNavigate('dashboard')}
            className="flex items-center gap-2 cursor-pointer"
          >
            <Logo size={28} />
            <span className="font-heading font-bold text-slate-900 text-sm">resmaker.ai</span>
          </button>
          <div className="flex items-center gap-1">
            {showUpgrade && creditsRemaining != null && (
              <button
                onClick={onUpgrade}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium cursor-pointer ${
                  creditsRemaining <= 0
                    ? 'bg-primary text-white'
                    : 'text-slate-600'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${
                  creditsRemaining <= 0 ? 'bg-white' : creditsRemaining <= 2 ? 'bg-slate-400' : 'bg-green-500'
                }`} />
                {creditsRemaining <= 0 ? 'Add' : creditsRemaining}
              </button>
            )}
            <button
              onClick={onOpenSettings}
              className="p-2 text-slate-500 cursor-pointer"
            >
              <Settings size={18} />
            </button>
          </div>
        </div>
      </header>
    </>
  );
}

export default Navigation;
